import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import EmailTrendChart from "./EmailTrendChart";
import SortableTableHead, { SortConfig, nextSort, sortData } from "./SortableTableHead";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Mail, Send, AlertTriangle, Ban, RefreshCw, Search, ChevronLeft, ChevronRight, Inbox, ShieldOff, Trash2, RotateCw, ShieldAlert, MessageSquareWarning, FileText, Download,
} from "lucide-react";
import EmailTemplatePreview from "./EmailTemplatePreview";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { format, subDays, subHours, parseISO } from "date-fns";

interface EmailLogEntry {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  created_at: string;
  metadata: any;
}

type TimeRange = "24h" | "7d" | "30d";

const PAGE_SIZE = 25;

const statusBadge = (status: string) => {
  switch (status) {
    case "sent":
      return <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/20">Sent</Badge>;
    case "pending":
      return <Badge variant="outline" className="text-amber-600 border-amber-400/40">Pending</Badge>;
    case "dlq":
    case "failed":
      return <Badge variant="destructive">Failed</Badge>;
    case "suppressed":
      return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 hover:bg-amber-500/20">Suppressed</Badge>;
    case "bounced":
      return <Badge variant="destructive" className="bg-red-500/15 text-red-700 border-red-500/30">Bounced</Badge>;
    case "complained":
      return <Badge variant="destructive" className="bg-orange-500/15 text-orange-700 border-orange-500/30">Complained</Badge>;
    default:
      return <Badge variant="secondary">{status}</Badge>;
  }
};

const templateLabel = (name: string) => name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

interface SuppressedEmail {
  id: string;
  email: string;
  reason: string;
  created_at: string;
  metadata: any;
}

const EmailDashboard = () => {
  const [logs, setLogs] = useState<EmailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<TimeRange>("7d");
  const [statusFilter, setStatusFilter] = useState("all");
  const [templateFilter, setTemplateFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [templates, setTemplates] = useState<string[]>([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, suppressed: 0, pending: 0, bounced: 0, complained: 0 });
  const [subView, setSubView] = useState("log");

  // Suppression list state
  const [suppressions, setSuppressions] = useState<SuppressedEmail[]>([]);
  const [suppressionLoading, setSuppressionLoading] = useState(false);
  const [suppressionSearch, setSuppressionSearch] = useState("");
  const [suppressionPage, setSuppressionPage] = useState(1);
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());

  // Deliverability state
  const [deliverabilityFilter, setDeliverabilityFilter] = useState<"all" | "bounced" | "complained">("all");
  const [deliverabilitySearch, setDeliverabilitySearch] = useState("");
  const [deliverabilityPage, setDeliverabilityPage] = useState(1);

  // Sort state for each table
  const [logSort, setLogSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });
  const [suppressionSort, setSuppressionSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });
  const [delivSort, setDelivSort] = useState<SortConfig>({ key: "created_at", direction: "desc" });

  const getStartDate = useCallback(() => {
    if (timeRange === "24h") return subHours(new Date(), 24);
    if (timeRange === "7d") return subDays(new Date(), 7);
    return subDays(new Date(), 30);
  }, [timeRange]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    const start = getStartDate().toISOString();

    let query = supabase
      .from("email_send_log")
      .select("*")
      .gte("created_at", start)
      .order("created_at", { ascending: false })
      .limit(1000);

    const { data, error } = await query;

    if (error) {
      console.error("Failed to fetch email logs", error);
      setLogs([]);
      setLoading(false);
      return;
    }

    const seen = new Map<string, EmailLogEntry>();
    const noMessageId: EmailLogEntry[] = [];
    (data || []).forEach((row: any) => {
      if (!row.message_id) {
        noMessageId.push(row);
        return;
      }
      const existing = seen.get(row.message_id);
      if (!existing || new Date(row.created_at) > new Date(existing.created_at)) {
        seen.set(row.message_id, row);
      }
    });

    const deduplicated = [...seen.values(), ...noMessageId];

    const uniqueTemplates = [...new Set(deduplicated.map((l) => l.template_name))].sort();
    setTemplates(uniqueTemplates);

    const s = { total: deduplicated.length, sent: 0, failed: 0, suppressed: 0, pending: 0, bounced: 0, complained: 0 };
    deduplicated.forEach((l) => {
      if (l.status === "sent") s.sent++;
      else if (l.status === "dlq" || l.status === "failed") s.failed++;
      else if (l.status === "suppressed") s.suppressed++;
      else if (l.status === "pending") s.pending++;
      else if (l.status === "bounced") s.bounced++;
      else if (l.status === "complained") s.complained++;
    });
    setStats(s);

    setLogs(deduplicated);
    setLoading(false);
    setCurrentPage(1);
  }, [getStartDate]);

  const fetchSuppressions = useCallback(async () => {
    setSuppressionLoading(true);
    const { data } = await supabase
      .from("suppressed_emails")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    setSuppressions((data as SuppressedEmail[]) || []);
    setSuppressionLoading(false);
    setSuppressionPage(1);
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { if (subView === "suppressions") fetchSuppressions(); }, [subView, fetchSuppressions]);

  const filtered = sortData(logs.filter((l) => {
    if (statusFilter !== "all") {
      if (statusFilter === "failed" && l.status !== "dlq" && l.status !== "failed") return false;
      if (statusFilter !== "failed" && l.status !== statusFilter) return false;
    }
    if (templateFilter !== "all" && l.template_name !== templateFilter) return false;
    if (search) {
      const term = search.toLowerCase();
      return l.recipient_email.toLowerCase().includes(term) || l.template_name.toLowerCase().includes(term);
    }
    return true;
  }), logSort);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Suppression filtering + sorting
  const filteredSuppressions = sortData(suppressions.filter((s) => {
    if (!suppressionSearch) return true;
    const term = suppressionSearch.toLowerCase();
    return s.email.toLowerCase().includes(term) || s.reason.toLowerCase().includes(term);
  }), suppressionSort);
  const suppressionTotalPages = Math.max(1, Math.ceil(filteredSuppressions.length / PAGE_SIZE));
  const suppressionSafePage = Math.min(suppressionPage, suppressionTotalPages);
  const paginatedSuppressions = filteredSuppressions.slice((suppressionSafePage - 1) * PAGE_SIZE, suppressionSafePage * PAGE_SIZE);

  const reasonBadge = (reason: string) => {
    switch (reason) {
      case "bounce":
        return <Badge variant="destructive">Bounce</Badge>;
      case "complaint":
        return <Badge className="bg-orange-500/15 text-orange-700 border-orange-500/30 hover:bg-orange-500/20">Complaint</Badge>;
      case "unsubscribe":
        return <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 hover:bg-amber-500/20">Unsubscribed</Badge>;
      default:
        return <Badge variant="secondary">{reason}</Badge>;
    }
  };

  const handleRetryEmail = async (log: EmailLogEntry) => {
    if (log.template_name === "auth_emails") {
      toast.error("Auth emails cannot be retried — the user should re-trigger the action");
      return;
    }
    setRetryingIds((prev) => new Set(prev).add(log.id));
    try {
      const { error } = await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: log.template_name,
          recipientEmail: log.recipient_email,
          idempotencyKey: `retry-${log.id}-${Date.now()}`,
        },
      });
      if (error) throw error;
      toast.success(`Retry queued for ${log.recipient_email}`);
      setTimeout(fetchLogs, 2000);
    } catch (err: any) {
      toast.error(`Retry failed: ${err.message || "Unknown error"}`);
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(log.id);
        return next;
      });
    }
  };

  const isRetryable = (status: string) => status === "dlq" || status === "failed";

  const handleRemoveSuppression = async (id: string, email: string) => {
    const { error } = await supabase.from("suppressed_emails").delete().eq("id", id);
    if (error) {
      toast.error("Failed to remove suppression");
      return;
    }
    toast.success(`Removed ${email} from suppression list`);
    setSuppressions((prev) => prev.filter((s) => s.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <Tabs value={subView} onValueChange={setSubView}>
        <TabsList className="flex overflow-x-auto no-scrollbar h-auto gap-1 p-1 bg-secondary w-full justify-start">
          <TabsTrigger value="log" className="shrink-0 min-h-11 gap-1.5"><Mail size={14} /> Email Log</TabsTrigger>
          <TabsTrigger value="suppressions" className="shrink-0 min-h-11 gap-1.5">
            <ShieldOff size={14} /> Suppression List
            {suppressions.length > 0 && <Badge variant="outline" className="text-xs ml-1 h-5 px-1.5">{suppressions.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="deliverability" className="shrink-0 min-h-11 gap-1.5">
            <ShieldAlert size={14} /> Deliverability
            {(stats.bounced + stats.complained) > 0 && <Badge variant="destructive" className="text-xs ml-1 h-5 px-1.5">{stats.bounced + stats.complained}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="templates" className="shrink-0 min-h-11 gap-1.5">
            <FileText size={14} /> Templates
          </TabsTrigger>
        </TabsList>

      <TabsContent value="log" className="space-y-6 mt-0">
      <div className="flex items-center gap-2">
        {(["24h", "7d", "30d"] as TimeRange[]).map((r) => (
          <Button
            key={r}
            variant={timeRange === r ? "default" : "outline"}
            size="sm"
            onClick={() => setTimeRange(r)}
          >
            {r === "24h" ? "Last 24h" : r === "7d" ? "Last 7 Days" : "Last 30 Days"}
          </Button>
        ))}
        <div className="ml-auto">
          <Button variant="outline" size="icon" onClick={fetchLogs}>
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Mail size={14} /> Total Emails</div>
            <p className="text-2xl font-bold mt-1">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-emerald-600 text-sm"><Send size={14} /> Sent</div>
            <p className="text-2xl font-bold mt-1">{stats.sent}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-600 text-sm"><Inbox size={14} /> Pending</div>
            <p className="text-2xl font-bold mt-1">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive text-sm"><AlertTriangle size={14} /> Failed</div>
            <p className="text-2xl font-bold mt-1">{stats.failed}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-amber-600 text-sm"><Ban size={14} /> Suppressed</div>
            <p className="text-2xl font-bold mt-1">{stats.suppressed}</p>
          </CardContent>
        </Card>
      </div>

      {!loading && logs.length > 0 && (
        <EmailTrendChart logs={logs} timeRange={timeRange} startDate={getStartDate()} />
      )}

      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="font-display text-xl">Email Log</CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search recipient or template..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-9 w-64"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="suppressed">Suppressed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={templateFilter} onValueChange={(v) => { setTemplateFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t} value={t}>{templateLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">No emails found for the selected filters.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                     <SortableTableHead label="Template" sortKey="template_name" currentSort={logSort} onSort={(k) => setLogSort(nextSort(logSort, k))} />
                     <SortableTableHead label="Recipient" sortKey="recipient_email" currentSort={logSort} onSort={(k) => setLogSort(nextSort(logSort, k))} />
                     <SortableTableHead label="Status" sortKey="status" currentSort={logSort} onSort={(k) => setLogSort(nextSort(logSort, k))} />
                     <SortableTableHead label="Timestamp" sortKey="created_at" currentSort={logSort} onSort={(k) => setLogSort(nextSort(logSort, k))} />
                     <SortableTableHead label="Error" sortKey="error_message" currentSort={logSort} onSort={(k) => setLogSort(nextSort(logSort, k))} />
                     <TableHead className="w-[70px]">Actions</TableHead>
                   </TableRow>
                 </TableHeader>
                 <TableBody>
                   {paginated.map((l) => (
                     <TableRow key={l.id}>
                       <TableCell className="text-sm font-medium">{templateLabel(l.template_name)}</TableCell>
                       <TableCell className="text-sm text-muted-foreground">{l.recipient_email}</TableCell>
                       <TableCell>{statusBadge(l.status)}</TableCell>
                       <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                         {format(parseISO(l.created_at), "MMM d, yyyy h:mm a")}
                       </TableCell>
                       <TableCell className="text-sm text-destructive max-w-[200px] truncate" title={l.error_message || ""}>
                         {l.error_message || "—"}
                       </TableCell>
                       <TableCell>
                         {isRetryable(l.status) && l.template_name !== "auth_emails" && (
                           <TooltipProvider>
                             <Tooltip>
                               <TooltipTrigger asChild>
                                 <Button
                                   variant="ghost"
                                   size="icon"
                                   className="h-8 w-8"
                                   disabled={retryingIds.has(l.id)}
                                   onClick={() => handleRetryEmail(l)}
                                 >
                                   <RotateCw size={14} className={retryingIds.has(l.id) ? "animate-spin" : ""} />
                                 </Button>
                               </TooltipTrigger>
                               <TooltipContent>Retry this email</TooltipContent>
                             </Tooltip>
                           </TooltipProvider>
                         )}
                       </TableCell>
                     </TableRow>
                   ))}
                 </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="icon" disabled={safePage <= 1} onClick={() => setCurrentPage((p) => p - 1)}>
                      <ChevronLeft size={14} />
                    </Button>
                    <span className="text-sm text-muted-foreground">Page {safePage} of {totalPages}</span>
                    <Button variant="outline" size="icon" disabled={safePage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                      <ChevronRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="suppressions" className="space-y-6 mt-0">
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="font-display text-xl">Suppressed Addresses</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search email or reason..."
                  value={suppressionSearch}
                  onChange={(e) => { setSuppressionSearch(e.target.value); setSuppressionPage(1); }}
                  className="pl-9 w-64"
                />
              </div>
              <Button variant="outline" size="icon" onClick={fetchSuppressions}>
                <RefreshCw size={14} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {suppressionLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredSuppressions.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No suppressed addresses found.</p>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                     <SortableTableHead label="Email" sortKey="email" currentSort={suppressionSort} onSort={(k) => setSuppressionSort(nextSort(suppressionSort, k))} />
                     <SortableTableHead label="Reason" sortKey="reason" currentSort={suppressionSort} onSort={(k) => setSuppressionSort(nextSort(suppressionSort, k))} />
                     <SortableTableHead label="Date Suppressed" sortKey="created_at" currentSort={suppressionSort} onSort={(k) => setSuppressionSort(nextSort(suppressionSort, k))} />
                     <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedSuppressions.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-sm font-medium">{s.email}</TableCell>
                        <TableCell>{reasonBadge(s.reason)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {format(parseISO(s.created_at), "MMM d, yyyy h:mm a")}
                        </TableCell>
                        <TableCell>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Remove suppression">
                                <Trash2 size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove suppression?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This will allow emails to be sent to <span className="font-medium">{s.email}</span> again. Are you sure you want to remove this suppression?
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleRemoveSuppression(s.id, s.email)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {suppressionTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {(suppressionSafePage - 1) * PAGE_SIZE + 1}–{Math.min(suppressionSafePage * PAGE_SIZE, filteredSuppressions.length)} of {filteredSuppressions.length}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" disabled={suppressionSafePage <= 1} onClick={() => setSuppressionPage((p) => p - 1)}>
                        <ChevronLeft size={14} />
                      </Button>
                      <span className="text-sm text-muted-foreground">Page {suppressionSafePage} of {suppressionTotalPages}</span>
                      <Button variant="outline" size="icon" disabled={suppressionSafePage >= suppressionTotalPages} onClick={() => setSuppressionPage((p) => p + 1)}>
                        <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </TabsContent>
      <TabsContent value="deliverability" className="space-y-6 mt-0">
        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground text-sm"><ShieldAlert size={14} /> Total Issues</div>
              <p className="text-2xl font-bold mt-1">{stats.bounced + stats.complained}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-destructive text-sm"><AlertTriangle size={14} /> Bounces</div>
              <p className="text-2xl font-bold mt-1">{stats.bounced}</p>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-sm" style={{ color: "hsl(var(--chart-4, 25 95% 53%))" }}><MessageSquareWarning size={14} /> Complaints</div>
              <p className="text-2xl font-bold mt-1">{stats.complained}</p>
            </CardContent>
          </Card>
        </div>

        {stats.sent > 0 && (
          <Card className="border-border">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground mb-2">Deliverability Rate</p>
              <div className="flex items-center gap-3">
                <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${Math.max(0, ((stats.sent - stats.bounced) / stats.sent) * 100)}%` }}
                  />
                </div>
                <span className="text-lg font-bold">
                  {(((stats.sent - stats.bounced) / stats.sent) * 100).toFixed(1)}%
                </span>
              </div>
              {stats.complained > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Complaint rate: {((stats.complained / stats.sent) * 100).toFixed(3)}%
                  {(stats.complained / stats.sent) > 0.001 && (
                    <span className="text-destructive ml-1">(above 0.1% threshold — action recommended)</span>
                  )}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Bounce/complaint log */}
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
           <CardTitle className="font-display text-xl">Bounce & Complaint Log</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => {
                  const rows = logs.filter((l) => {
                    if (l.status !== "bounced" && l.status !== "complained") return false;
                    if (deliverabilityFilter !== "all" && l.status !== deliverabilityFilter) return false;
                    if (deliverabilitySearch) {
                      return l.recipient_email.toLowerCase().includes(deliverabilitySearch.toLowerCase());
                    }
                    return true;
                  });
                  if (rows.length === 0) { toast.info("No data to export"); return; }
                  const header = "Recipient,Type,Error,Timestamp\n";
                  const csv = header + rows.map((r) =>
                    `"${r.recipient_email}","${r.status}","${(r.error_message || "").replace(/"/g, '""')}","${format(parseISO(r.created_at), "yyyy-MM-dd HH:mm:ss")}"`
                  ).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `deliverability-${format(new Date(), "yyyy-MM-dd")}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success(`Exported ${rows.length} records`);
                }}
              >
                <Download size={14} /> Export CSV
              </Button>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search recipient..."
                  value={deliverabilitySearch}
                  onChange={(e) => { setDeliverabilitySearch(e.target.value); setDeliverabilityPage(1); }}
                  className="pl-9 w-64"
                />
              </div>
              <Select value={deliverabilityFilter} onValueChange={(v) => { setDeliverabilityFilter(v as any); setDeliverabilityPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Issues</SelectItem>
                  <SelectItem value="bounced">Bounces</SelectItem>
                  <SelectItem value="complained">Complaints</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {(() => {
              const deliverabilityLogs = sortData(logs.filter((l) => {
                if (l.status !== "bounced" && l.status !== "complained") return false;
                if (deliverabilityFilter !== "all" && l.status !== deliverabilityFilter) return false;
                if (deliverabilitySearch) {
                  return l.recipient_email.toLowerCase().includes(deliverabilitySearch.toLowerCase());
                }
                return true;
              }), delivSort);
              const dTotalPages = Math.max(1, Math.ceil(deliverabilityLogs.length / PAGE_SIZE));
              const dSafePage = Math.min(deliverabilityPage, dTotalPages);
              const dPaginated = deliverabilityLogs.slice((dSafePage - 1) * PAGE_SIZE, dSafePage * PAGE_SIZE);

              if (loading) {
                return (
                  <div className="flex justify-center py-12">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                );
              }
              if (deliverabilityLogs.length === 0) {
                return <p className="text-center text-muted-foreground py-12">No bounce or complaint events found. Great deliverability! 🎉</p>;
              }
              return (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead label="Recipient" sortKey="recipient_email" currentSort={delivSort} onSort={(k) => setDelivSort(nextSort(delivSort, k))} />
                        <SortableTableHead label="Type" sortKey="status" currentSort={delivSort} onSort={(k) => setDelivSort(nextSort(delivSort, k))} />
                        <SortableTableHead label="Template" sortKey="template_name" currentSort={delivSort} onSort={(k) => setDelivSort(nextSort(delivSort, k))} />
                        <SortableTableHead label="Timestamp" sortKey="created_at" currentSort={delivSort} onSort={(k) => setDelivSort(nextSort(delivSort, k))} />
                        <SortableTableHead label="Details" sortKey="error_message" currentSort={delivSort} onSort={(k) => setDelivSort(nextSort(delivSort, k))} />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dPaginated.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="text-sm font-medium">{l.recipient_email}</TableCell>
                          <TableCell>{statusBadge(l.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{templateLabel(l.template_name)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {format(parseISO(l.created_at), "MMM d, yyyy h:mm a")}
                          </TableCell>
                          <TableCell className="text-sm text-destructive max-w-[200px] truncate" title={l.error_message || ""}>
                            {l.error_message || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {dTotalPages > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <p className="text-sm text-muted-foreground">
                        Showing {(dSafePage - 1) * PAGE_SIZE + 1}–{Math.min(dSafePage * PAGE_SIZE, deliverabilityLogs.length)} of {deliverabilityLogs.length}
                      </p>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" disabled={dSafePage <= 1} onClick={() => setDeliverabilityPage((p) => p - 1)}>
                          <ChevronLeft size={14} />
                        </Button>
                        <span className="text-sm text-muted-foreground">Page {dSafePage} of {dTotalPages}</span>
                        <Button variant="outline" size="icon" disabled={dSafePage >= dTotalPages} onClick={() => setDeliverabilityPage((p) => p + 1)}>
                          <ChevronRight size={14} />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="templates" className="space-y-6 mt-0">
        <EmailTemplatePreview />
      </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailDashboard;
